import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import InfoItem from "components/common/detailsPage/InfoItem";
import { CustomerStatusBadge } from "components/common/StatusBadge";
import React, { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatUTCDate } from "utils/utils";
import ChangePassword from "./ChangePassword";
import { SquarePen } from "lucide-react";
import EditProfile from "./EditProfile";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import HorizontalScroller from "components/common/HorizontalScroller";

function Profile() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { loading, authUserDetails, rolePermissions } = useSelector(
    (state) => state.Auth,
  );
  const { _id, name, email, accountType, phoneNumber, status, createdAt } =
    authUserDetails || {};

  const infoFields = [
    {
      label: t("Id"),
      render : <HorizontalScroller>{_id || "-"}</HorizontalScroller>,
      col: { lg: 6 },
    },
    {
      label: t("Name"),
      value: name || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Phone Number"),
      value: phoneNumber || "-",
      col: { lg: 6 },
    },
    {
      label: t("Email Address"),
      value: email || "",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Status"),
      render: <CustomerStatusBadge status={status} t={t} />,
      col: { lg: 6 },
    },
    {
      label: t("Created Date"),
      value: formatUTCDate(createdAt),
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Permissions"),
      value: t("Full Permission"),
      col: { lg: 6 },
      show: accountType === "superadmin",
    },
  ];
  const [selected, setSelected] = useState({});
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [editProfileModal, setEditProfileModal] = useState(false);
  const shimmerLayout = [
    {
      col: 6,
      blocks: [
        { rows: 3, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 6,
      blocks: [{ rows: 4, cols: 2 }],
    }
  ];

  useEffect(() => {
    if (rolePermissions?.roleId?.permissions) {
      const mapped = {};

      rolePermissions.roleId.permissions.forEach((module) => {
        mapped[module.module] = module.permission
          .filter((p) => p.allowed)
          .map((p) => p.key);
      });

      setSelected(mapped);
    }
  }, [rolePermissions]);
  if (loading) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("Profile details")} />
        <DetailsShimmer layout={shimmerLayout} />{" "}
      </div>
    );
  }

  return (
    <div className="page-content common_details_page admin_profile">
      <DetailsTitle title={t("Profile details")} />
      <Row>
        {/* LEFT COLUMN — Profile Details */}
        <Col lg={7} md={9}>
          <div className="common_card h-100">
            <div className="d-flex justify-content-between align-items-center profile_details_actions">
              <div className="d-flex gap-2 align-items-center">
                <h2 className="title_20">{t("User Details")}</h2>
                <SquarePen onClick={() => setEditProfileModal(true)} />
              </div>
              <button
                className="main_btn"
                onClick={() => setChangePasswordModal(true)}
              >
                {t("Change Password")}
              </button>
            </div>
            <div className="general_details_wrapper">
              <Row id="general_detail_row">
                {infoFields
                  .filter((item) => item.show !== false)
                  .map((item, index) => {
                    if (item.isSpacer) {
                      return <div key={index} className="mt-4 w-100" />;
                    }

                    return (
                      <InfoItem
                        key={index}
                        label={item.label}
                        value={item.value}
                        col={item.col}
                      >
                        {item.render}
                      </InfoItem>
                    );
                  })}
              </Row>
            </div>
          </div>
        </Col>

        {/* RIGHT COLUMN — Permissions */}
        {rolePermissions && rolePermissions?.hasFullPermission === false && (
          <Col lg={6}>
            <div className="common_card h-100">
              <h2 className="title_20">{t("Permissions Details")}</h2>

              <Row>
                {rolePermissions?.permissions?.map((module) => (
                  <Col md={6} key={module.id}>
                    <div className="single_permission">
                      <h6>{t(module.module)}</h6>

                      <div className="permission_wrapper">
                        {module?.permissions?.map((perm) => (
                          <Form.Check
                            key={perm.id}
                            type="checkbox"
                            label={t(perm)}
                            checked
                            disabled
                          />
                        ))}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Col>
        )}
      </Row>
      <Modal
        show={changePasswordModal}
        onHide={() => setChangePasswordModal(false)}
        centered
        className="common_modal "
      >
        <h2 className="modal_title">{t("Change Password")}</h2>
        <div className="content">
          <ChangePassword
            onCancel={() => setChangePasswordModal(false)}
            onSuccess={() => setChangePasswordModal(false)}
          />
        </div>
      </Modal>
      <Modal
        show={editProfileModal}
        onHide={() => setEditProfileModal(false)}
        centered
        className="common_modal "
      >
        <h2 className="modal_title">{t("Edit Profile")}</h2>
        <div className="content">
          <EditProfile
            onCancel={() => setEditProfileModal(false)}
            onSuccess={() => setEditProfileModal(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

export default Profile;
